import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import '../styles/ProfilePage.css';
import AuthContext from '../contexts/AuthContext';

const ProfilePage = () => {
  const { user, updateUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [profileUpdated, setProfileUpdated] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setProfileData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || ''
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateProfileForm = () => {
    const newErrors = {};
    
    if (!profileData.name.trim()) newErrors.name = 'Name is required';
    // Email validation removed since email is now read-only
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = () => {
    const newErrors = {};
    
    if (!profileData.currentPassword) newErrors.currentPassword = 'Current password is required';
    
    if (!profileData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (profileData.newPassword.length < 6) {
      newErrors.newPassword = 'New password must be at least 6 characters';
    }
    
    if (profileData.newPassword !== profileData.confirmNewPassword) {
      newErrors.confirmNewPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (!validateProfileForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('Updating user profile with data:', { 
        name: profileData.name, 
        phone: profileData.phone 
      });
      
      const response = await userService.updateUserProfile({
        name: profileData.name,
        phone: profileData.phone
      });
      
      console.log('Profile update response:', response);
      
      // If we received a response with data
      if (response && (response.success || response.user)) {
        // Update local user data
        updateUser({
          ...user,
          name: profileData.name,
          phone: profileData.phone
          // Email is preserved from the existing user object
        });
        
        setProfileUpdated(true);
        setTimeout(() => setProfileUpdated(false), 3000);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrors(prev => ({
        ...prev,
        profile: error.response?.data?.message || 'Failed to update profile'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await userService.changePassword({
        currentPassword: profileData.currentPassword,
        newPassword: profileData.newPassword
      });
      
      if (response.success) {
        // Clear password fields
        setProfileData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: ''
        }));
        
        setPasswordUpdated(true);
        setTimeout(() => setPasswordUpdated(false), 3000);
      }
    } catch (error) {
      console.error('Error updating password:', error);
      setErrors(prev => ({
        ...prev,
        password: error.response?.data?.message || 'Failed to update password'
      }));
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="profile-container">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="header">
        <h1>My Profile</h1>
        <Link 
          to="/settings" 
          className="back-btn"
        >
          ← Back
        </Link>
      </div>

      <div className="profile-content">
        <div className="profile-info-card">
          <div className="profile-header">
            <div className="avatar">
              {user.name ? user.name.charAt(0).toUpperCase() : '?'}
            </div>
            <div className="profile-title">
              <h2>{user.name}</h2>
              <div className="profile-meta">
                <span className="role-badge">{user.role}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="profile-update-card">
          <h2>Update Your Profile</h2>
          
          {profileUpdated && (
            <div className="success-message">Profile updated successfully!</div>
          )}
          
          {errors.profile && (
            <div className="error-message">{errors.profile}</div>
          )}
          
          <form onSubmit={handleUpdateProfile}>
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={profileData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <div className="error-text">{errors.name}</div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={profileData.email}
                readOnly
                disabled
                className="disabled-field"
              />
              <small className="helper-text">
                Email address cannot be changed
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={profileData.phone}
                onChange={handleChange}
              />
            </div>
            
            <button 
              type="submit"
              className="update-btn"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>
        </div>

        <div className="password-update-card">
          <h2>Change Your Password</h2>
          
          {passwordUpdated && (
            <div className="success-message">Password updated successfully!</div>
          )}
          
          {errors.password && (
            <div className="error-message">{errors.password}</div>
          )}
          
          <form onSubmit={handleUpdatePassword}>
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={profileData.currentPassword}
                onChange={handleChange}
                className={errors.currentPassword ? 'error' : ''}
              />
              {errors.currentPassword && (
                <div className="error-text">{errors.currentPassword}</div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={profileData.newPassword}
                onChange={handleChange}
                className={errors.newPassword ? 'error' : ''}
              />
              {errors.newPassword && (
                <div className="error-text">{errors.newPassword}</div>
              )}
              <small className="helper-text">
                Password must be at least 6 characters
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmNewPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmNewPassword"
                name="confirmNewPassword"
                value={profileData.confirmNewPassword}
                onChange={handleChange}
                className={errors.confirmNewPassword ? 'error' : ''}
              />
              {errors.confirmNewPassword && (
                <div className="error-text">{errors.confirmNewPassword}</div>
              )}
            </div>
            
            <button 
              type="submit"
              className="update-btn"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;